import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCheckCommunityLibraryByUrlLazyQuery } from "@/generated/graphql";
import { useToast } from "@/hooks/use-toast";
import { VideoMetadata, getVideoMetadata, detectVideoPlatform } from "@/lib/video-metadata";
import { getDropboxErrorCode, getDropboxErrorKey } from '@/lib/dropbox/errors';
import { getShareImportErrorKey, isShareImportPlatform, isShareImportProRequired } from '@/lib/shareImport/shareImport';
import { unsupportedSourceDescriptionKey } from '@/lib/frameio/unsupportedSourceDescriptionKey';
import { MAX_VIDEO_DURATION_SECONDS } from "@/lib/video-limits";
import { validateUrlVideoMetadata } from "@/lib/upload/validateUrlVideoMetadata";
import { resolveLibrarySourceAccess } from "@/lib/library/resolveLibrarySourceAccess";
import {
  buildCampaignLibraryDeepLink,
  buildCommunityLibraryDeepLink,
  type CommunityLibraryMatch,
} from "@/lib/library/buildCommunityLibraryDeepLink";
import type { CheckCommunityLibraryByUrlQuery } from "@/generated/graphql";

interface UseUrlUploadOptions {
  maxVideoSeconds?: number;
  hasProAccess?: boolean;
  onProRequired?: () => void;
}

export function useUrlUpload(options?: UseUrlUploadOptions) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const hasProAccess = options?.hasProAccess ?? false;

  const maxDuration = options?.maxVideoSeconds || MAX_VIDEO_DURATION_SECONDS;

  const [videoUrl, setVideoUrl] = useState("");
  const [urlVideoMetadata, setUrlVideoMetadata] = useState<VideoMetadata | null>(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [communityMatch, setCommunityMatch] = useState<CommunityLibraryMatch | null>(null);

  const [checkCommunityLibraryByUrl] = useCheckCommunityLibraryByUrlLazyQuery({ fetchPolicy: 'network-only' });

  const handleUrlUpload = async (url: string) => {
    if (!url) return;

    const platform = detectVideoPlatform(url);
    const access = resolveLibrarySourceAccess({ platform, hasProAccess });
    if (access === 'unsupported') {
      toast({
        title: t('library.unsupportedSource'),
        description: t(unsupportedSourceDescriptionKey({ url: url, platform })),
        variant: "destructive",
      });
      return;
    }
    if (access === 'pro_required') {
      options?.onProRequired?.();
      return;
    }

    setIsLoadingMetadata(true);
    try {
      const metadata = await getVideoMetadata(url);
      const invalid = validateUrlVideoMetadata(metadata, { maxDurationSeconds: maxDuration });
      if (invalid) {
        toast({
          title: t(invalid.titleKey),
          description: t(invalid.descriptionKey, invalid.descriptionParams),
          variant: "destructive",
        });
        return;
      }

      // Private Dropbox/Frame.io/MediaSilo imports never enter community discovery.
      if (platform !== 'dropbox' && !isShareImportPlatform(platform)) {
        try {
          const { data: checkData } = await checkCommunityLibraryByUrl({
            variables: { url },
          });
          const matchedLibrary = checkData?.checkCommunityLibraryByUrl;
          if (matchedLibrary) {
            setCommunityMatch(mapCommunityLibraryMatch(matchedLibrary));
            return;
          }
        } catch {
          // If the check fails, proceed normally
        }
      }

      setUrlVideoMetadata(metadata);
    } catch (error) {
      if (platform === 'dropbox') {
        if (getDropboxErrorCode(error) === 'DROPBOX_PRO_REQUIRED') options?.onProRequired?.();
        else toast({ title: t('errors.videoAnalysisError'), description: t(getDropboxErrorKey(error)), variant: 'destructive' });
        return;
      }
      if (isShareImportPlatform(platform)) {
        if (isShareImportProRequired(error)) options?.onProRequired?.();
        else toast({ title: t('errors.videoAnalysisError'), description: t(getShareImportErrorKey(error, platform)), variant: 'destructive' });
        return;
      }
      console.error('Failed to get video metadata:', error);
      toast({ title: t('errors.videoAnalysisError'), description: t('errors.checkVideoLink'), variant: "destructive" });
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const resetUpload = () => {
    setUrlVideoMetadata(null);
    setVideoUrl("");
  };

  const navigateToCommunityMatch = () => {
    if (!communityMatch) return;
    const campaignId = communityMatch.active_cpm_campaigns?.[0]?.id;
    const { pathname, search, state } = campaignId
      ? buildCampaignLibraryDeepLink(campaignId, communityMatch)
      : buildCommunityLibraryDeepLink(communityMatch);
    navigate({ pathname, search: `?${search}` }, { state });
    setCommunityMatch(null);
  };

  return {
    videoUrl,
    setVideoUrl,
    urlVideoMetadata,
    isLoadingMetadata,
    communityMatch,
    setCommunityMatch,
    handleUrlUpload,
    resetUpload,
    navigateToCommunityMatch,
  };
}

function mapCommunityLibraryMatch(
  library: NonNullable<CheckCommunityLibraryByUrlQuery['checkCommunityLibraryByUrl']>,
): CommunityLibraryMatch {
  return {
    id: library.id,
    name: library.name,
    base_video_url: library.base_video_url,
    duration_seconds: library.duration_seconds,
    format: library.format,
    language: library.language,
    start_time: library.start_time,
    end_time: library.end_time,
    active_cpm_campaigns: library.active_cpm_campaigns,
    channelId: library.youtube_channels?.[0]?.channel_id ?? null,
  };
}
